import { FBRef } from "@binale-tech/shared";
import { child, get, onDisconnect, onValue, ref, remove, serverTimestamp, set, update } from "firebase/database";

import { database } from "@domain/api/firebase/firebase";

export type SessionRevokeReason = "login" | "manual";

export type UserSession = {
    device: string;
    startedAt: number;
    revokedAt?: number;
    revokedBy?: string;
    revokedReason?: SessionRevokeReason;
};

export type UserSessionEntry = UserSession & { sessionId: string };

const userRootRef = (uid: string) => ref(database, `${FBRef.UsersSessions}/${uid}`);
const sessionsRef = (uid: string) => child(userRootRef(uid), "sessions");
const ownSessionRef = (uid: string, sessionId: string) => child(sessionsRef(uid), sessionId);
const singleSessionRef = (uid: string) => child(userRootRef(uid), "settings/singleSession");

export const registerSession = async (uid: string, sessionId: string, device: string): Promise<void> => {
    const sessionRef = ownSessionRef(uid, sessionId);
    // Presence: the row is the liveness signal, so the server drops it when this browser goes away.
    await onDisconnect(sessionRef).remove();
    await set(sessionRef, { device, startedAt: serverTimestamp() });
};

export const revokeOtherSessions = async (
    uid: string,
    currentSessionId: string,
    byDevice: string,
    reason: SessionRevokeReason,
): Promise<void> => {
    const snapshot = await get(sessionsRef(uid));
    const all = (snapshot.val() ?? {}) as Record<string, UserSession>;
    const updates: Record<string, unknown> = {};
    Object.keys(all)
        .filter((id) => id !== currentSessionId)
        .forEach((id) => {
            updates[`${id}/revokedAt`] = serverTimestamp();
            updates[`${id}/revokedBy`] = byDevice;
            updates[`${id}/revokedReason`] = reason;
        });
    if (Object.keys(updates).length === 0) {
        return;
    }
    await update(sessionsRef(uid), updates);
};

export const revokeSession = async (uid: string, sessionId: string, byDevice: string): Promise<void> => {
    await update(ownSessionRef(uid, sessionId), {
        revokedAt: serverTimestamp(),
        revokedBy: byDevice,
        revokedReason: "manual" satisfies SessionRevokeReason,
    });
};

export const removeOwnSession = async (uid: string, sessionId: string): Promise<void> => {
    await remove(ownSessionRef(uid, sessionId));
};

export const subscribeOwnSession = (
    uid: string,
    sessionId: string,
    handlers: { onRevoked: (session: UserSession) => void; onMissing: () => void },
): (() => void) =>
    onValue(ownSessionRef(uid, sessionId), (snapshot) => {
        const value = snapshot.val() as UserSession | null;
        if (!value) {
            // Never a sign-out: a disconnect blip fires the server-side removal, so re-register instead.
            handlers.onMissing();
            return;
        }
        if (value.revokedAt) {
            handlers.onRevoked(value);
        }
    });

export const subscribeSessions = (uid: string, cb: (entries: UserSessionEntry[]) => void): (() => void) =>
    onValue(sessionsRef(uid), (snapshot) => {
        const all = (snapshot.val() ?? {}) as Record<string, UserSession>;
        cb(
            Object.entries(all)
                .filter(([, session]) => !session.revokedAt)
                .map(([sessionId, session]) => ({ sessionId, ...session })),
        );
    });

export const subscribeConnection = (cb: (connected: boolean) => void): (() => void) =>
    onValue(ref(database, ".info/connected"), (snapshot) => cb(snapshot.val() === true));

export const readSingleSession = async (uid: string): Promise<boolean> => {
    const snapshot = await get(singleSessionRef(uid));
    return snapshot.val() !== false;
};

export const subscribeSingleSession = (uid: string, cb: (enabled: boolean) => void): (() => void) =>
    onValue(singleSessionRef(uid), (snapshot) => cb(snapshot.val() !== false));

export const setSingleSession = async (uid: string, enabled: boolean): Promise<void> => {
    await set(singleSessionRef(uid), enabled);
};

export const cleanupLegacySession = async (uid: string): Promise<void> => {
    const snapshot = await get(userRootRef(uid));
    const value = snapshot.val() as Record<string, unknown> | null;
    if (typeof value?.sessionId !== "string") {
        return;
    }
    await remove(child(userRootRef(uid), "sessionId"));
    await remove(child(userRootRef(uid), "device"));
};
